import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { createClient } from "@/lib/supabase/client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Wallet, Loader2, Mail, Lock, User, Moon, Sun } from "lucide-react"
import { toast } from "sonner"
import { useTheme } from "next-themes"

export default function AuthPage() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const supabase = createClient()
  const { theme, setTheme } = useTheme()

  // Login state
  const [loginEmail, setLoginEmail] = useState("")
  const [loginPassword, setLoginPassword] = useState("")

  // Signup state
  const [signupEmail, setSignupEmail] = useState("")
  const [signupPassword, setSignupPassword] = useState("")
  const [signupName, setSignupName] = useState("")

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      })

      if (error) {
        toast.error(error.message)
        return
      }

      toast.success("Welcome back!")
      navigate("/home")
    } catch (error) {
      console.error("Login error:", error)
      toast.error("An unexpected error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!signupName.trim()) {
      toast.error("Please enter your name")
      return
    }

    // Strong password validation
    const { validatePassword } = await import("@/lib/utils/password-validation")
    const validation = validatePassword(signupPassword)

    if (!validation.isValid) {
      toast.error(validation.errors[0]) // Show first error
      return
    }

    setIsLoading(true)

    try {
      // Sign up the user
      const { data: authData, error: signupError } = await supabase.auth.signUp({
        email: signupEmail,
        password: signupPassword,
        options: {
          data: {
            full_name: signupName,
          },
        },
      })

      if (signupError) {
        toast.error(signupError.message)
        return
      }

      if (!authData.user) {
        toast.error("Failed to create account")
        return
      }

      toast.success("Account created successfully!")
      navigate("/home")
    } catch (error) {
      console.error("Signup error:", error)
      toast.error("An unexpected error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50/30 via-white to-green-50/20 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 p-4">
      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 right-4 rounded-full"
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      >
        {theme === "dark" ? (
          <Sun className="h-5 w-5" />
        ) : (
          <Moon className="h-5 w-5" />
        )}
        <span className="sr-only">Toggle theme</span>
      </Button>

      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="mb-4 inline-block">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[var(--brand-amber)] opacity-20 blur-xl animate-pulse" />
              <picture>
                <source srcSet="/logo.avif" type="image/avif" />
                <source srcSet="/logo.webp" type="image/webp" />
                <img
                  src="/logo.webp"
                  alt="Money Bees Logo"
                  width="96"
                  height="96"
                  fetchPriority="high"
                  decoding="async"
                  className="relative w-24 h-24 mx-auto rounded-full shadow-md ring-2 ring-[var(--brand-amber)]/30"
                />
              </picture>
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2">Money Bees</h1>
          <p className="text-muted-foreground">Manage your finances with ease</p>
        </div>

        <Tabs defaultValue="login" className="w-full min-h-[480px]">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger
              value="login"
              className="data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-green)] data-[state=active]:text-foreground data-[state=active]:bg-[var(--brand-green)]/20 dark:data-[state=active]:bg-[var(--brand-green)]/15 transition-all duration-200"
            >
              Login
            </TabsTrigger>
            <TabsTrigger
              value="signup"
              className="data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-green)] data-[state=active]:text-foreground data-[state=active]:bg-[var(--brand-green)]/20 dark:data-[state=active]:bg-[var(--brand-green)]/15 transition-all duration-200"
            >
              Sign Up
            </TabsTrigger>
          </TabsList>

          {/* Login Tab */}
          <TabsContent
            value="login"
            className="data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-4 duration-300"
          >
            <Card>
              <CardHeader>
                <CardTitle>Welcome back</CardTitle>
                <CardDescription>Enter your credentials to access your account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full hover:shadow-[0_0_20px_rgba(var(--brand-amber-light),0.3)] transition-shadow duration-300"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Signup Tab */}
          <TabsContent
            value="signup"
            className="data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-4 duration-300"
          >
            <Card>
              <CardHeader>
                <CardTitle>Create your account</CardTitle>
                <CardDescription>Start managing your finances today</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-name"
                        type="text"
                        placeholder="John Doe"
                        value={signupName}
                        onChange={(e) => setSignupName(e.target.value)}
                        className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="you@example.com"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder="••••••••••••"
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                        required
                        minLength={12}
                        disabled={isLoading}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Must be at least 12 characters with uppercase, lowercase, number, and special character
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full hover:shadow-[0_0_20px_rgba(var(--brand-amber-light),0.3)] transition-shadow duration-300"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  )
}
